// The two helpers that talk to the database (storage signing, the tenant
// document checklist). Kept apart from helpers.js so that file stays pure:
// the unit tests load helpers.js under Node, where the browser's Supabase
// client (../supabase) cannot be created -- the wizard suite's live half had
// been crashing on exactly that import before it ran a single check
// (2026-10-07).
import { supabase } from "../supabase";
import { pmError } from "./errors";
import { proxiedStorageUrl, hasAllRequiredTenantDocs } from "./helpers";

export async function getSignedUrl(bucket, filePath, expiresIn = 3600) {
  if (!filePath) return "";
  const { data, error } = await supabase.storage.from(bucket).createSignedUrl(filePath, expiresIn);
  if (error) { pmError("PM-8006", { raw: error, context: "signed URL for " + filePath, silent: true }); return ""; }
  return proxiedStorageUrl(data?.signedUrl || "");
}

// Recompute and persist tenants.doc_status based on the tenant's current documents.
// - Preserves "exception_approved" (admin-approved override)
// - Otherwise sets "complete" when all required docs are present, else "pending_docs"
//
// Accepts either (companyId, tenantName) for legacy callers OR
// (companyId, { tenantId, tenantName, property }) for the preferred
// id-scoped path. documents.tenant_id doesn't exist in this schema, so
// the docs scan still has to filter by name — but we also scope by
// property when provided so two tenants sharing a name at different
// addresses don't cross-count uploads.
export async function recomputeTenantDocStatus(companyId, tenantOrOpts) {
  if (!companyId || !tenantOrOpts) return;
  const opts = typeof tenantOrOpts === "string" ? { tenantName: tenantOrOpts } : (tenantOrOpts || {});
  const { tenantId, tenantName, property } = opts;
  if (!tenantId && !tenantName) return;
  let tRowsQ = supabase.from("tenants").select("id, doc_status").eq("company_id", companyId).is("archived_at", null);
  tRowsQ = tenantId ? tRowsQ.eq("id", tenantId) : tRowsQ.ilike("name", tenantName);
  const { data: rows } = await tRowsQ;
  const targets = (rows || []).filter(r => r.doc_status !== "exception_approved");
  if (targets.length === 0) return;
  // documents.tenant_id is authoritative where present. Without it, a
  // name-only match counted a NAMESAKE's uploads toward this tenant's
  // document checklist, marking them "complete" on someone else's files.
  // Bounded. This drives a tenant's document checklist; 500 is far past any
  // real tenant's file count and makes the limit a decision rather than
  // Supabase's silent 1000.
  let docsQ = supabase.from("documents").select("name, type").eq("company_id", companyId).is("archived_at", null).limit(500);
  if (tenantId) docsQ = docsQ.eq("tenant_id", tenantId);
  else if (tenantName) docsQ = docsQ.eq("tenant", tenantName);
  if (property) docsQ = docsQ.eq("property", property);
  const { data: docs } = await docsQ;
  const nextStatus = hasAllRequiredTenantDocs(docs) ? "complete" : "pending_docs";
  await supabase
    .from("tenants")
    .update({ doc_status: nextStatus })
    .in("id", targets.map(r => r.id))
    .eq("company_id", companyId);
}
